/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { MrpcMessageType, WebSocketError, WebSocketErrorCode } from "./types";
import type { IWebSocketMessage } from "./types";

/**
 * WebSocket message serializer — converts between typed objects and IWebSocketMessage.
 *
 * **REQ-SER-03:** Implements serialize and deserialize operations.
 */
export class WebSocketSerializer {
  private static readonly TAG: string = "WebSocketSerializer";

  /**
   * Deserialize an IWebSocketMessage body into a typed object.
   *
   * @param message - The structured message with a JSON body
   * @returns The parsed JSON payload
   * @throws WebSocketError if the body is not valid JSON
   */
  public static deserialize(
    message: IWebSocketMessage
  ): Record<string, unknown> {
    try {
      if (!message.body || message.body.trim().length === 0) {
        return {};
      }

      const parsed: unknown = JSON.parse(message.body);
      if (typeof parsed !== "object" || parsed === null) {
        throw new Error("Body is not a JSON object");
      }

      const opForLog: string =
        message.operationName === null ? "null" : message.operationName;
      Log.d(
        WebSocketSerializer.TAG,
        `deserialize(): op=${opForLog}, rpcId=${message.id}`
      );

      return parsed as Record<string, unknown>;
    } catch (error: unknown) {
      Log.e(WebSocketSerializer.TAG, "deserialize()", error);
      throw new WebSocketError(
        WebSocketErrorCode.DESERIALIZE_ERROR,
        `Failed to deserialize message body: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Serialize a response object into an IWebSocketMessage.
   *
   * Used for outbound responses — echoes the original request's
   * rpcId and originRequestId for correlation.
   *
   * @param originalMessage - The inbound request message being responded to
   * @param responseObject - The typed response payload
   * @returns A response IWebSocketMessage ready for enveloping
   */
  public static serialize(
    originalMessage: IWebSocketMessage,
    responseObject: Record<string, unknown>
  ): IWebSocketMessage {
    const body: string = JSON.stringify(responseObject);

    Log.d(
      WebSocketSerializer.TAG,
      `serialize(): rpcId=${originalMessage.id}, bodyLen=${body.length}`
    );

    return {
      type: MrpcMessageType.RESPONSE,
      operationName: null,
      id: originalMessage.id,
      originRequestId: originalMessage.originRequestId,
      headers: {},
      body: body,
      isResponseExpected: false
    };
  }

  /**
   * Build an error response message.
   *
   * @param originalMessage - The inbound request that caused the error
   * @param errorType - Error type string (e.g. "internalError", "featureNotEnabled")
   * @param errorText - Human-readable error description
   * @returns A response IWebSocketMessage with the error payload
   */
  public static serializeError(
    originalMessage: IWebSocketMessage,
    errorType: string,
    errorText: string
  ): IWebSocketMessage {
    const errorBody: Record<string, unknown> = {
      type: "error",
      errorCode: errorType,
      text: errorText
    };

    Log.w(
      WebSocketSerializer.TAG,
      `serializeError(): rpcId=${originalMessage.id}, error=${errorType}`
    );

    return {
      type: MrpcMessageType.RESPONSE,
      operationName: null,
      id: originalMessage.id,
      originRequestId: originalMessage.originRequestId,
      headers: {},
      body: JSON.stringify(errorBody),
      isResponseExpected: false
    };
  }
}
